import React, { useCallback, useEffect, useRef, useReducer, useState } from 'react';
import '../styles/TaskDescriptionEditor.css';
import { createEditor, Descendant, Transforms, Editor } from '@editablejs/models';
import { EditableProvider, ContentEditable, withEditable, useEditableStatic } from '@editablejs/editor';
import {
  withPlugins,
  MarkEditor,
  HeadingEditor,
  BlockquoteEditor,
  OrderedListEditor,
  UnorderedListEditor,
  TaskListEditor,
  IndentEditor,
  LinkEditor,
  ImageEditor,
  FontColorEditor,
  BackgroundColorEditor,
  FontSizeEditor,
  AlignEditor,
  AlignValue,
  HrEditor,
  TableEditor,
  CodeBlockEditor,
} from '@editablejs/plugins';
import type { MarkFormat } from '@editablejs/plugin-mark';

// ── Serialisation ─────────────────────────────────────────────────────────
const SENTINEL = '__editablejs__:';

export function valueToStorageString(value: Descendant[]): string {
  return SENTINEL + JSON.stringify(value);
}

export function storageStringToValue(raw: string): Descendant[] {
  if (!raw) return defaultValue();
  if (raw.startsWith(SENTINEL)) {
    try { return JSON.parse(raw.slice(SENTINEL.length)) as Descendant[]; }
    catch { return defaultValue(); }
  }
  // Legacy HTML or old broken JSON sentinel — extract plain text
  const text = raw.replace(/\{["']?__editablejs["']?[^}]*\}/g, '')
                   .replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text ? [{ type: 'paragraph', children: [{ text }] } as Descendant] : defaultValue();
}

function defaultValue(): Descendant[] {
  return [{ type: 'paragraph', children: [{ text: '' }] } as Descendant];
}

// ── Toolbar primitives ─────────────────────────────────────────────────────
const TBtn: React.FC<{
  active?: boolean;
  title: string;
  disabled?: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}> = ({ active, title, disabled, onMouseDown, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    className={`rte-btn${active ? ' rte-btn--active' : ''}`}
    onMouseDown={e => { e.preventDefault(); if (!disabled) onMouseDown(e); }}
  >
    {children}
  </button>
);

const Divider = () => <span className="rte-divider" />;

// ── Toolbar ────────────────────────────────────────────────────────────────
const Toolbar: React.FC = () => {
  const editor = useEditableStatic();
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const orig = editor.onChange;
    editor.onChange = () => { (orig as () => void).call(editor); forceUpdate(); };
    return () => { editor.onChange = orig; };
  }, [editor]);

  const mark = (m: MarkFormat) => ({ active: (() => { try { return MarkEditor.isActive(editor, m); } catch { return false; } })() });
  const toggleMark = (m: MarkFormat) => { try { MarkEditor.toggle(editor, m); } catch {} forceUpdate(); };

  const activeFontSize = (() => { try { return FontSizeEditor.queryActive(editor) || '14px'; } catch { return '14px'; } })();
  const activeAlign   = (() => { try { return AlignEditor.queryActive(editor); } catch { return 'left'; } })();

  return (
    <div className="rte-toolbar">

      {/* Undo / Redo */}
      <TBtn title="Undo (Ctrl+Z)" onMouseDown={() => { try { (editor as any).undo?.(); } catch {} }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 7v6h6"/><path d="M3 13C5.5 7 10 5 15 7s7 8 5 13"/></svg>
      </TBtn>
      <TBtn title="Redo (Ctrl+Y)" onMouseDown={() => { try { (editor as any).redo?.(); } catch {} }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 7v6h-6"/><path d="M21 13C18.5 7 14 5 9 7S2 15 4 20"/></svg>
      </TBtn>

      <Divider />

      {/* Marks */}
      <TBtn title="Bold (Ctrl+B)" {...mark('bold')} onMouseDown={() => toggleMark('bold')}><strong>B</strong></TBtn>
      <TBtn title="Italic (Ctrl+I)" {...mark('italic')} onMouseDown={() => toggleMark('italic')}><em>I</em></TBtn>
      <TBtn title="Underline (Ctrl+U)" {...mark('underline')} onMouseDown={() => toggleMark('underline')}><u>U</u></TBtn>
      <TBtn title="Strikethrough" {...mark('strikethrough')} onMouseDown={() => toggleMark('strikethrough')}><s>S</s></TBtn>
      <TBtn title="Inline code" {...mark('code')} onMouseDown={() => toggleMark('code')}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
      </TBtn>
      <TBtn title="Subscript" {...mark('sub')} onMouseDown={() => toggleMark('sub')}>
        X<sub style={{ fontSize: '8px' }}>z</sub>
      </TBtn>
      <TBtn title="Superscript" {...mark('sup')} onMouseDown={() => toggleMark('sup')}>
        X<sup style={{ fontSize: '8px' }}>z</sup>
      </TBtn>

      <Divider />

      {/* Font colour with dropdown */}
      <label className="rte-color-wrap" title="Text colour">
        <span className="rte-color-icon">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/></svg>
          <span className="rte-color-dot" style={{ background: '#ef4444' }} />
        </span>
        <input type="color" className="rte-color-input" defaultValue="#ef4444"
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { try { FontColorEditor.toggle(editor, e.target.value); } catch {} forceUpdate(); }} />
      </label>

      {/* Highlight */}
      <label className="rte-color-wrap" title="Highlight">
        <span className="rte-color-icon">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"/><path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1 1 6.65 2 8 .99.67-.9.9-2 .65-3.26A3.01 3.01 0 0 0 7.07 15z"/></svg>
          <span className="rte-color-dot" style={{ background: '#fef08a' }} />
        </span>
        <input type="color" className="rte-color-input" defaultValue="#fef08a"
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { try { BackgroundColorEditor.toggle(editor, e.target.value); } catch {} forceUpdate(); }} />
      </label>

      <Divider />

      {/* Font size */}
      <div className="rte-fontsize-wrap">
        <input
          type="number"
          className="rte-fontsize-input"
          title="Font size"
          min={8} max={96}
          defaultValue={14}
          onMouseDown={e => e.stopPropagation()}
          onBlur={e => {
            const sz = parseInt(e.target.value);
            if (sz >= 8 && sz <= 96) { try { FontSizeEditor.toggle(editor, sz + 'px'); } catch {} forceUpdate(); }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const sz = parseInt((e.target as HTMLInputElement).value);
              if (sz >= 8 && sz <= 96) { try { FontSizeEditor.toggle(editor, sz + 'px'); } catch {} (e.target as HTMLElement).blur(); }
            }
          }}
        />
        <span className="rte-fontsize-unit">px</span>
      </div>

      <Divider />

      {/* Heading / Paragraph */}
      <select
        className="rte-heading-select"
        value={(() => { try { return HeadingEditor.queryActive(editor) || 'paragraph'; } catch { return 'paragraph'; } })()}
        onMouseDown={e => e.stopPropagation()}
        onChange={e => {
          const val = e.target.value;
          try {
            if (val === 'paragraph') {
              HeadingEditor.toggle(editor, 'paragraph' as any);
            } else {
              HeadingEditor.toggle(editor, val as any);
            }
          } catch {}
          forceUpdate();
        }}
      >
        <option value="paragraph">Paragraph</option>
        <option value="heading-one">Heading 1</option>
        <option value="heading-two">Heading 2</option>
        <option value="heading-three">Heading 3</option>
        <option value="heading-four">Heading 4</option>
        <option value="heading-five">Heading 5</option>
        <option value="heading-six">Heading 6</option>
      </select>

      <Divider />

      {/* Link */}
      <TBtn title="Insert link" onMouseDown={() => {
        const url = window.prompt('Enter URL:');
        if (url) { try { LinkEditor.insert(editor, { href: url }); } catch {} forceUpdate(); }
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
      </TBtn>

      {/* Image — file upload from device */}
      <TBtn title="Insert image" onMouseDown={e => { e.preventDefault(); fileInputRef.current?.click(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
      </TBtn>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) { try { ImageEditor.insert(editor, { file }); } catch {} forceUpdate(); }
          e.target.value = '';
        }}
      />

      {/* Blockquote */}
      <TBtn title="Blockquote" onMouseDown={() => { try { BlockquoteEditor.toggle(editor); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
          <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
        </svg>
      </TBtn>

      {/* Unordered list */}
      <TBtn title="Bullet list" onMouseDown={() => { try { UnorderedListEditor.toggle(editor); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/>
          <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/>
          <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/>
          <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/>
        </svg>
      </TBtn>

      {/* Ordered list */}
      <TBtn title="Numbered list" onMouseDown={() => { try { OrderedListEditor.toggle(editor); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
          <path d="M4 6H3v4h1M4 12H3v2h1v2H3" strokeWidth="1.5"/>
        </svg>
      </TBtn>

      {/* Task list */}
      <TBtn title="Task list" onMouseDown={() => { try { TaskListEditor.toggle(editor); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="3" y="5" width="6" height="6" rx="1"/><polyline points="4 8 6 10 8 6"/>
          <line x1="13" y1="8" x2="20" y2="8"/>
          <rect x="3" y="14" width="6" height="6" rx="1"/>
          <line x1="13" y1="17" x2="20" y2="17"/>
        </svg>
      </TBtn>

      {/* Delete table (visible only when inside a table) */}
      {TableEditor.isActive(editor) && (
        <TBtn title="Delete table" onMouseDown={e => {
          e.preventDefault();
          try {
            const tableEntries = Array.from(Editor.nodes(editor, { match: (n: any) => n.type === 'table' }));
            if (tableEntries.length > 0) Transforms.removeNodes(editor, { at: tableEntries[0][1] });
          } catch {}
          forceUpdate();
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
            <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
            <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444"/>
          </svg>
        </TBtn>
      )}

      {/* Table */}
      <TBtn title="Insert table" onMouseDown={e => {
        e.preventDefault();
        // focus editor first so selection exists, then insert
        setTimeout(() => {
          try { TableEditor.insert(editor, { rows: 3, cols: 3 }); } catch {}
          forceUpdate();
        }, 0);
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
          <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
        </svg>
      </TBtn>

      <Divider />

      {/* Align group */}
      <TBtn title="Align left" active={activeAlign === 'left'} onMouseDown={() => { try { AlignEditor.toggle(editor, AlignValue.Left); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/>
        </svg>
      </TBtn>
      <TBtn title="Align center" active={activeAlign === 'center'} onMouseDown={() => { try { AlignEditor.toggle(editor, AlignValue.Center); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
        </svg>
      </TBtn>
      <TBtn title="Align right" active={activeAlign === 'right'} onMouseDown={() => { try { AlignEditor.toggle(editor, AlignValue.Right); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/>
        </svg>
      </TBtn>
      <TBtn title="Justify" active={activeAlign === 'justify'} onMouseDown={() => { try { AlignEditor.toggle(editor, AlignValue.Justify); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </TBtn>

      <Divider />

      {/* Indent / Outdent */}
      <TBtn title="Indent" onMouseDown={() => { try { IndentEditor.toggle(editor); } catch {} }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="8" x2="21" y2="8"/><line x1="3" y1="16" x2="21" y2="16"/>
          <polyline points="9 12 6 15 9 18"/>
        </svg>
      </TBtn>
      <TBtn title="Outdent" onMouseDown={() => { try { (editor as any).toggleOutdent?.(); } catch {} }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="8" x2="21" y2="8"/><line x1="3" y1="16" x2="21" y2="16"/>
          <polyline points="6 12 9 15 6 18"/>
        </svg>
      </TBtn>

      {/* Horizontal rule */}
      <TBtn title="Horizontal rule" onMouseDown={() => { try { HrEditor.insert(editor); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="3" y1="12" x2="21" y2="12"/>
        </svg>
      </TBtn>

      {/* Code block */}
      <TBtn title="Code block" onMouseDown={() => { try { CodeBlockEditor.insert(editor); } catch {} forceUpdate(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
          <rect x="2" y="2" width="20" height="20" rx="2" strokeWidth="1.5" fill="none"/>
        </svg>
      </TBtn>

    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
interface Props {
  htmlValue: string;
  onChange: (stored: string) => void;
  placeholder?: string;
}

function makeEditor() {
  const base = withEditable(createEditor());
  return withPlugins(base, {
    mark: {},
    heading: {},
    blockquote: {},
    orderedList: {},
    unorderedList: {},
    taskList: {},
    indent: {},
    link: {},
    image: {},
    fontColor: {},
    backgroundColor: {},
    fontSize: {},
    align: {},
    hr: {},
    table: {},
    codeBlock: {},
  });
}

const TaskDescriptionEditor: React.FC<Props> = ({ htmlValue, onChange, placeholder }) => {
  // useState(() => ...) runs the initialiser ONCE per mount — never cached
  // across remounts (unlike useMemo). key={taskId} from parent guarantees
  // a fresh mount per task, so editor and initialValue are always correct.
  const [editor] = useState(() => makeEditor());
  const [initialValue] = useState(() => storageStringToValue(htmlValue));

  const handleChange = useCallback((value: Descendant[]) => {
    onChange(valueToStorageString(value));
  }, [onChange]);

  return (
    <div className="rte-wrapper">
      <EditableProvider editor={editor} value={initialValue} onChange={handleChange}>
        <Toolbar />
        <div className="rte-content-wrap rte-content">
          <ContentEditable
            placeholder={placeholder ?? 'Add a clear and detailed description...'}
          />
        </div>
      </EditableProvider>
    </div>
  );
};

export default TaskDescriptionEditor;
